import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { AlertContext } from '../contexts/alert-context';
import type { Event } from '../data/event';
import {
  onboardingEventsQueryOptions,
  onboardingOverviewQueryOptions,
} from '../query/query-options';
import { useAdminService } from './admin-service';

const EVENTS_LIMIT = 5000;

export function useOnboardingStoreEvents(storeId: string) {
  const { addErrorAlert } = useContext(AlertContext);
  const adminService = useAdminService();
  const queryClient = useQueryClient();

  const options = onboardingEventsQueryOptions(storeId);

  const { data, isLoading } = useQuery({
    ...options,
    queryFn: async () => {
      const cached = queryClient.getQueryData<Event[]>(options.queryKey);

      const opts =
        cached && cached.length > 0
          ? { after: cached[0].event_id, limit: EVENTS_LIMIT }
          : { limit: EVENTS_LIMIT };

      try {
        const fresh = await adminService.getOnboardingStoreEvents(storeId, opts);
        if (cached && cached.length > 0) {
          // Empty delta: keep the same reference so consumers don't re-render.
          if (fresh.events.length === 0) {
            return cached;
          }

          // Events are newest-first; prepend the delta to keep order.
          return [...fresh.events, ...cached];
        }

        return fresh.events;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        addErrorAlert(`Failed to fetch timeline for store ${storeId}: ${message}`);
        throw error;
      }
    },
  });

  const events = data ?? [];

  return {
    events,
    loading: events.length === 0 && isLoading,
  };
}

export function useOnboardingOverview() {
  const { addErrorAlert } = useContext(AlertContext);
  const adminService = useAdminService();

  return useQuery({
    ...onboardingOverviewQueryOptions(),
    queryFn: async () => {
      return adminService.getOnboardingOverview().catch((error: Error) => {
        addErrorAlert(`Failed to fetch onboarding overview: ${error.message}`);
        throw error;
      });
    },
  });
}
